import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { supabase } from '@/lib/supabase';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ErrorBubble } from '@/components/ui/ErrorBubble';
import { maskPhone } from '@/lib/validation';
import { Turnstile } from '@marsidev/react-turnstile';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { trackEvent, trackUserIdentity } from '@/lib/gtm';
import { SocialLoginButtons } from '@/components/ui/SocialLoginButtons';
import { validateAndResolvePlan, createStripeCheckoutSession } from '@/lib/stripePlans';
import { submitHubspotSignupForm, trackHubspotLogin, formatHubspotDateTime } from '@/services/hubspotService';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '0x4AAAAAACr6Jt9kXRTg--6h';

const signupSchema = z.object({
    firstname: z.string().min(2, { message: "O nome deve ter no mínimo 2 caracteres." }),
    email: z.string().email({ message: "Por favor, insira um e-mail válido." }),
    phone: z.string().optional().refine(val => !val || val.length >= 14, {
        message: "Por favor, insira um telefone válido com DDD."
    }),
    password: z.string().min(6, { message: "A senha deve ter no mínimo 6 caracteres." })
});

type SignupFormData = z.infer<typeof signupSchema>;


interface SignupFormProps {
    onToggleView: () => void;
    isActive?: boolean;
}

export const SignupForm: React.FC<SignupFormProps> = ({ onToggleView, isActive = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const turnstileRef = useRef<any>(null);

    // Sanitiza e resolve o plano a partir da querystring da URL
    const selectedPlan = useMemo(() => {
        return validateAndResolvePlan(
            searchParams.get('plan'),
            searchParams.get('billing') || searchParams.get('interval')
        );
    }, [searchParams]);

    const [signupCaptchaToken, setSignupCaptchaToken] = useState<string>('');
    const [pendingSignupSubmit, setPendingSignupSubmit] = useState(false);

    // Signup State
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [loadingSignup, setLoadingSignup] = useState(false);
    const [signupSuccess, setSignupSuccess] = useState('');
    const [generalError, setGeneralError] = useState<string>('');
    const [tsId] = useState(() => `ts-signup-${Math.random().toString(36).slice(2, 9)}`);
    const [renderTs, setRenderTs] = useState(false);

    useEffect(() => {
        if (isActive) {
            const timer = setTimeout(() => setRenderTs(true), 700);
            return () => clearTimeout(timer);
        } else {
            setRenderTs(false);
        }
    }, [isActive]);

    const { register, handleSubmit, control, formState: { errors }, watch } = useForm<SignupFormData>({
        resolver: zodResolver(signupSchema),
        defaultValues: {
            firstname: '',
            email: '',
            phone: '',
            password: ''
        }
    });

    const formData = watch();

    const onSubmit = async (data: SignupFormData) => {
        if (!signupCaptchaToken) {
            setPendingSignupSubmit(true);
            return;
        }

        setPendingSignupSubmit(false);
        setLoadingSignup(true);
        setGeneralError('');
        setSignupSuccess('');

        const fullName = data.firstname.trim();
        const nameParts = fullName.split(/\s+/).filter(Boolean);
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        const nowFormatted = formatHubspotDateTime();

        // 1. Submissão direta e oficial para a API de Formulários do HubSpot (com notificação por e-mail e dados completos)
        submitHubspotSignupForm({
            email: data.email,
            firstname: firstName,
            lastname: lastName,
            phone: data.phone || null,
            total_logins: 1,
            ultimo_login: nowFormatted,
        }).catch((err) => console.warn('[HubSpot] Erro no envio assíncrono (fail-safe):', err));

        // 2. Disparo imediato para a fila de telemetria do HubSpot (_hsq) no navegador
        if (typeof window !== 'undefined') {
            window._hsq = window._hsq || [];
            window._hsq.push(['identify', {
                email: data.email,
                firstname: firstName,
                lastname: lastName,
                ultimo_login: nowFormatted,
                total_logins: 1,
                total_de_logins: 1,
                hs_lead_status: 'NEW',
                lifecyclestage: 'subscriber',
            }]);
            window._hsq.push(['trackPageView']);
        }

        try {
            const { data: authData, error } = await supabase.auth.signUp({
                email: data.email,
                password: data.password,
                options: {
                    data: {
                        full_name: fullName,
                        telefone: data.phone || null,
                    },
                    captchaToken: signupCaptchaToken
                }
            });
            if (error) throw error;

            trackHubspotLogin({
                email: data.email,
                userId: authData.user?.id,
            });

            if (authData.user) {
                trackUserIdentity(authData.user, {
                    full_name: fullName,
                    subscription_tier: selectedPlan ? (selectedPlan.plan.id as 'basic' | 'pro' | 'business') : 'trial',
                    trial_ends_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
                });
            }
            
            if (authData.session) {
                trackEvent('sign_up', { plan: selectedPlan ? selectedPlan.plan.id : 'free_trial' });

                if (selectedPlan) {
                    setSignupSuccess(`Conta criada com sucesso! Redirecionando para o checkout seguro do Plano ${selectedPlan.plan.name}...`);
                    try {
                        const checkoutUrl = await createStripeCheckoutSession(selectedPlan.priceId);
                        if (checkoutUrl) {
                            window.location.href = checkoutUrl;
                            return;
                        }
                        throw new Error('URL de checkout não retornada.');
                    } catch (checkoutErr: unknown) {
                        console.error('Erro ao redirecionar para checkout da Stripe:', checkoutErr);
                        // Fallback resiliente: conta criada no Supabase, redireciona para dashboard
                        setSignupSuccess('Conta criada! Redirecionando para o dashboard...');
                        setTimeout(() => {
                            navigate('/dashboard');
                        }, 1500);
                    }
                } else {
                    setSignupSuccess('Cadastro realizado com sucesso! Conectando...');
                    setTimeout(() => {
                        navigate('/dashboard');
                    }, 1000);
                }
            } else {
                setSignupSuccess('Cadastro realizado! Verifique seu e-mail para confirmar a conta.');
                trackEvent('sign_up');
                setLoadingSignup(false);
            }
            
        } catch (err: unknown) {
            setSignupCaptchaToken('');
            setSignupSuccess(''); // Clear success message if there's an error
            
            if (err instanceof Error) {
                if (err.message === 'User already registered') {
                    setGeneralError('Este e-mail já está cadastrado.');
                } else if (err.message.includes('Password should be')) {
                    setGeneralError('A senha deve ter no mínimo 6 caracteres.');
                } else {
                    setGeneralError('Ocorreu um erro ao realizar o cadastro. Tente novamente.');
                }
            } else {
                setGeneralError('Ocorreu um erro desconhecido.');
            }
            setLoadingSignup(false);
        }
    };

    useEffect(() => {
        if (pendingSignupSubmit && signupCaptchaToken) {
            setPendingSignupSubmit(false);
            onSubmit(formData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingSignupSubmit, signupCaptchaToken]);

    return (
        <>
        <div className="w-1/2 p-4 sm:p-6 flex flex-col justify-center bg-indigo-50/40 relative h-full">
            {/* Botão de voltar */}
            <button 
                onClick={() => {
                    setGeneralError('');
                    onToggleView();
                }}
                type="button"
                className="absolute top-4 left-4 p-2 text-slate-400 hover:text-indigo-600 hover:bg-white rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                title="Voltar para login"
                disabled={loadingSignup || !!signupSuccess}
            >
                <ArrowLeft size={20} />
            </button>

            <div className="flex flex-col items-center mb-3 sm:mb-4 mt-2 sm:mt-0">
                <h2 className="text-xl font-bold text-slate-900 tracking-tight text-center">Comece agora</h2>
                <p className="text-slate-600 mt-1 text-xs text-center font-medium">
                    Crie sua conta e configure seu espaço em segundos.
                </p>
                {selectedPlan && (
                    <div className="mt-2.5 w-full px-3 py-1.5 rounded-lg bg-indigo-50 border border-indigo-200/80 flex items-center justify-between text-xs animate-in fade-in">
                        <div className="flex items-center gap-1.5 font-bold text-indigo-900">
                            <Sparkles size={14} className="text-indigo-600 shrink-0" />
                            <span>Plano {selectedPlan.plan.name}</span>
                        </div>
                        <span className="text-[11px] font-semibold text-indigo-700 bg-white border border-indigo-100 px-2 py-0.5 rounded-md shadow-xs">
                            {selectedPlan.interval === 'year' ? 'Anual' : 'Mensal'}
                        </span>
                    </div>
                )}
            </div>

            <form id="form-cadastro-usuario" name="Cadastro PersonaLab" onSubmit={handleSubmit(onSubmit)} className="space-y-2">
                <SocialLoginButtons 
                    mode="signup" 
                    disabled={loadingSignup || !!signupSuccess}
                    onError={setGeneralError}
                    termsAccepted={termsAccepted}
                    onBeforeOAuth={() => {
                        if (selectedPlan) {
                            sessionStorage.setItem('pending_checkout_plan', JSON.stringify({
                                planId: selectedPlan.plan.id,
                                priceId: selectedPlan.priceId,
                            }));
                        }
                    }}
                />

                <div className="space-y-1 relative">
                    <label htmlFor="signup-name" className="text-sm font-bold text-slate-700">Nome Completo</label>
                    <input id="signup-name" type="text" autoComplete="name" disabled={loadingSignup || !!signupSuccess}
                        className={`w-full px-4 py-2 border bg-white rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all placeholder:text-slate-400 font-medium text-sm disabled:opacity-70 disabled:bg-slate-50 disabled:cursor-not-allowed ${errors.firstname ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-indigo-200/60 focus:border-indigo-500'}`}
                        placeholder="João Silva"
                        {...register('firstname')}
                    />
                    <ErrorBubble message={errors.firstname?.message} />
                </div>
                <div className="space-y-1 relative">
                    <label htmlFor="signup-email" className="text-sm font-bold text-slate-700">E-mail corporativo ou pessoal</label>
                    <input id="signup-email" type="email" autoComplete="email" disabled={loadingSignup || !!signupSuccess}
                        className={`w-full px-4 py-2 border bg-white rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all placeholder:text-slate-400 font-medium text-sm disabled:opacity-70 disabled:bg-slate-50 disabled:cursor-not-allowed ${errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-indigo-200/60 focus:border-indigo-500'}`}
                        placeholder="voce@empresa.com"
                        {...register('email')}
                    />
                    <ErrorBubble message={errors.email?.message} />
                </div>
                <div className="space-y-1 relative">
                    <label htmlFor="signup-phone" className="text-sm font-bold text-slate-700">Telefone <span className="text-slate-400 font-normal">(Opcional)</span></label>
                    <Controller
                        name="phone"
                        control={control}
                        render={({ field }) => (
                            <input id="signup-phone" type="tel" autoComplete="tel" disabled={loadingSignup || !!signupSuccess}
                                className={`w-full px-4 py-2 border bg-white rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all placeholder:text-slate-400 font-medium text-sm disabled:opacity-70 disabled:bg-slate-50 disabled:cursor-not-allowed ${errors.phone ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-indigo-200/60 focus:border-indigo-500'}`}
                                placeholder="(11) 99999-9999" maxLength={15}
                                value={field.value}
                                onChange={(e) => field.onChange(maskPhone(e.target.value))}
                            />
                        )}
                    />
                    <ErrorBubble message={errors.phone?.message} />
                </div>
                <div className="space-y-1 relative">
                    <label htmlFor="signup-password" className="text-sm font-bold text-slate-700">Crie uma senha forte</label>
                    <input id="signup-password" type="password" autoComplete="new-password" disabled={loadingSignup || !!signupSuccess}
                        className={`w-full px-4 py-2 border bg-white rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all placeholder:text-slate-400 font-medium text-sm disabled:opacity-70 disabled:bg-slate-50 disabled:cursor-not-allowed ${errors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-indigo-200/60 focus:border-indigo-500'}`}
                        placeholder="Mínimo de 6 caracteres"
                        {...register('password')}
                    />
                    <ErrorBubble message={errors.password?.message} />
                </div>

                <div className="flex items-start gap-3 pt-1">
                    <input
                        id="terms-accept"
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        disabled={loadingSignup || !!signupSuccess}
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-indigo-600 cursor-pointer disabled:cursor-not-allowed shrink-0"
                    />
                    <label htmlFor="terms-accept" className="text-xs text-slate-600 leading-relaxed cursor-pointer select-none">
                        Ao criar minha conta, concordo com os{' '}
                        <a
                            href="https://chatpersonalab.com.br/termos"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-indigo-600 hover:text-indigo-800 underline underline-offset-2"
                            onClick={(e) => e.stopPropagation()}
                        >
                            Termos de Uso
                        </a>
                        {' '}e a{' '}
                        <a
                            href="https://chatpersonalab.com.br/privacidade"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-indigo-600 hover:text-indigo-800 underline underline-offset-2"
                            onClick={(e) => e.stopPropagation()}
                        >
                            Política de Privacidade
                        </a>
                        {' '}da PersonaLab.
                    </label>
                </div>

                {generalError && (
                    <div className="text-red-700 text-sm bg-red-50 p-3 rounded-xl border border-red-100 font-medium animate-in fade-in slide-in-from-top-1 text-center">
                        {generalError}
                    </div>
                )}

                {signupSuccess && (
                    <div className="text-emerald-700 text-sm bg-emerald-50 p-3 rounded-xl border border-emerald-100 font-medium animate-in fade-in slide-in-from-top-1 text-center">
                        {signupSuccess}
                    </div>
                )}

                <Button 
                    type="submit" 
                    fullWidth 
                    loading={loadingSignup || !!signupSuccess || pendingSignupSubmit} 
                    disabled={loadingSignup || !!signupSuccess || pendingSignupSubmit || !termsAccepted} 
                    className={`py-2.5 text-sm font-bold shadow-lg rounded-xl bg-indigo-600 hover:bg-indigo-700 mt-2 transition-opacity duration-200 ${
                        !termsAccepted ? 'opacity-40 shadow-none cursor-not-allowed' : 'opacity-100 shadow-indigo-600/20'
                    }`}
                >
                    {pendingSignupSubmit
                        ? 'Verificando sistema...'
                        : (loadingSignup || !!signupSuccess)
                            ? (selectedPlan ? 'Preparando checkout seguro...' : 'Conectando...')
                            : selectedPlan
                                ? `Criar Conta e Assinar Plano ${selectedPlan.plan.name}`
                                : 'Criar Conta Gratuita'}
                </Button>

            </form>

        </div>

        {createPortal(
            <div className="fixed bottom-4 right-4 z-[9999] transform scale-75 origin-bottom-right pointer-events-auto">
                {renderTs && (
                    <Turnstile 
                        ref={turnstileRef}
                        key={location.key ? `signup-ts-${location.key}` : tsId}
                        id={tsId}
                        options={{ size: 'flexible' }}
                        siteKey={TURNSTILE_SITE_KEY} 
                        onSuccess={(token) => {
                            setSignupCaptchaToken(token);
                        }}
                        onError={() => {
                            setPendingSignupSubmit(false);
                        }}
                    />
                )}
            </div>,
            document.body
        )}
        </>
    );
};
